import React, { useState, useEffect } from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText, MdFilterList
} from './ThemeLayoutHelpers';
import { MdMenu, MdClose, MdArrowForward, MdOutlineSpa, MdOutlineAutoAwesome, MdOutlineCheckCircle, MdOutlineEco, MdOutlineFavoriteBorder, MdStar } from 'react-icons/md';

export default function LuxeGlowLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#8c765c"; // Elegant bronze/gold accent
  const buttonColor = T.buttonColor || "#a88e72"; // Soft warm clay/rose gold
  const text = p.storefrontText || {};
  
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeIngredient, setActiveIngredient] = useState("squalane");

  // Track scroll position for header blur
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const copy = getHeroContent(p, {
    eyebrow: "Pure • Organic • Radiant",
    title: "Nature's Science for Luxury Skin.",
    subtitle: "Nourish your skin with ultra-pure botanical formulations crafted to reveal your natural, glowing radiance.",
    primaryCta: "Shop Skincare",
    secondaryCta: "Our Story",
  });

  const navigateTo = (page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "shop") {
      p.setActiveTab("products");
      setTimeout(() => {
        const el = document.getElementById("catalog-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#faf6f0] text-[#3c3530] antialiased" style={{ fontFamily: T.fontFamily || "DM Sans, sans-serif" }}>
      {/* Top Banner */}
      <div className="w-full bg-[#f1eae0] border-b border-[#e5dcd0] py-2 px-4 text-center text-xs font-medium tracking-wide text-[#705e4c]">
        <span>Join the club to get 10% off your first purchase and free shipping. </span>
        <button onClick={(e) => navigateTo("shop", e)} className="underline hover:text-[#3c3530] ml-1 font-bold inline-flex items-center gap-0.5">
          Shop collection <MdArrowForward className="text-[10px]" />
        </button>
      </div>

      {/* Glassmorphic Nav */}
      <nav className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? "bg-[#faf6f0]/90 backdrop-blur-md border-b border-[#e5dcd0] shadow-sm" 
          : "bg-transparent border-b border-transparent"
      }`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border="border border-[#e5dcd0]" size="h-9 w-9" />
            <div>
              <p className="text-base font-bold tracking-tight text-[#3c3530] font-serif">{p.storeInfo?.businessName}</p>
              <p className="text-[9px] text-[#8c765c] tracking-widest font-bold uppercase mt-0.5">{p.storeInfo?.businessCategory || "Organic Beauty"}</p>
            </div>
          </div>

          {/* Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {[
              ["home", "Home"],
              ["shop", "Shop Skincare"],
              ["details", "About LuxeGlow"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`text-xs font-semibold tracking-wider uppercase transition-colors hover:text-[#8c765c] ${
                  activePage === page 
                    ? "text-[#8c765c]" 
                    : "text-gray-500"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search, Wishlist, Cart */}
          <div className="flex items-center gap-3.5">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="hidden md:block w-48" />
            
            <button onClick={p.handleGoToWishlist} className="relative p-2 rounded-full hover:bg-[#ebdcc8] text-[#3c3530] transition-colors" title="Wishlist">
              <FaHeart className="text-base text-[#8c765c]" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 text-[9px] font-bold rounded-full text-white flex items-center justify-center bg-[#8c765c] shadow-sm animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>

            <CartBtn p={p} />
            {p.userMenuElement}

            <button 
              className="md:hidden p-2 rounded-full hover:bg-gray-200/50 text-[#3c3530]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Drawer */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#faf6f0] border-b border-[#e5dcd0] shadow-xl p-6 flex flex-col gap-4 animate-fadeIn md:hidden">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="w-full mb-2" />
            {[
              ["home", "Home"],
              ["shop", "Shop Skincare"],
              ["details", "About Us"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`py-2 text-left text-xs font-bold tracking-wider uppercase border-b border-[#ebdcc8] hover:text-[#8c765c] ${
                  activePage === page ? "text-[#8c765c] font-bold" : "text-gray-500"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Main Area */}
      <main className="flex-grow">
        {activePage === "home" && (
          <>
            {/* Split Luxe Hero Banner */}
            <section className="max-w-6xl mx-auto px-6 pt-6 pb-12">
              <div className="relative rounded-[2.5rem] overflow-hidden bg-white border border-[#e5dcd0] shadow-md p-8 md:p-14 flex flex-col md:flex-row items-center gap-10 min-h-[460px]">
                {/* Background organic blur circle */}
                <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-[#f5ebe0]/60 blur-[130px] pointer-events-none" />
                <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-[#e3d5ca]/50 blur-[130px] pointer-events-none" />

                {/* Left content */}
                <div className="flex-1 text-left relative z-10">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#8c765c] mb-3">
                    <MdOutlineSpa className="text-base text-[#8c765c]" />
                    <InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText>
                  </span>
                  <h1 className="text-4xl md:text-5xl font-bold font-serif text-[#3c3530] leading-tight mb-4 tracking-tight">
                    <InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText>
                  </h1>
                  <p className="text-gray-500 text-sm leading-relaxed mb-8 max-w-lg">
                    <InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText>
                  </p>
                  
                  <div className="flex flex-wrap gap-4">
                    <button 
                      onClick={() => navigateTo("shop")}
                      className="px-8 py-3 rounded-full text-xs font-bold tracking-wider uppercase text-white shadow-md hover:shadow-lg active:scale-98 transition-all"
                      style={{ backgroundColor: buttonColor }}
                    >
                      <InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText>
                    </button>
                    <button 
                      onClick={() => { p.setActiveTab("details"); navigateTo("about"); }}
                      className="px-8 py-3 rounded-full text-xs font-bold tracking-wider uppercase text-[#3c3530] border border-[#e5dcd0] bg-transparent hover:bg-[#faf6f0] transition-colors"
                    >
                      <InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText>
                    </button>
                  </div>
                </div>

                {/* Right image */}
                <div className="flex-1 w-full relative z-10 self-stretch rounded-3xl overflow-hidden border border-[#e5dcd0]/50 shadow-inner bg-[#faf6f0]">
                  <HeroImage p={p} themeId="luxeglow" className="h-[280px] md:h-full">
                    <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-white/30 to-transparent pointer-events-none" />
                  </HeroImage>
                </div>
              </div>
            </section>

            {/* Skincare Active Ingredients Section */}
            <section className="max-w-6xl mx-auto px-6 py-12">
              <div className="bg-[#fcfaf7] border border-[#ebdcc8] rounded-[2.5rem] p-8 md:p-12 shadow-sm">
                <div className="max-w-xl text-left mb-10">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#8c765c]">ORGANIC CHEMISTRY</span>
                  <h2 className="text-3xl font-bold font-serif text-[#3c3530] mt-1">Sourced by nature. Proven by science.</h2>
                  <p className="text-gray-500 text-xs mt-2">Every formulation is engineered using potent plant-derived bio-actives to deliver safe, luxury dermatological results.</p>
                </div>

                <div className="flex flex-col md:flex-row gap-8 items-stretch">
                  {/* Selector tabs */}
                  <div className="flex md:flex-col gap-2 overflow-x-auto pb-3 md:pb-0 border-b md:border-b-0 md:border-r border-[#ebdcc8] w-full md:w-56 shrink-0 text-left">
                    {[
                      { id: "squalane", label: "Vegan Squalane", icon: <MdOutlineEco /> },
                      { id: "rosehip", label: "Rosehip Extract", icon: <MdOutlineSpa /> },
                      { id: "peptides", label: "Multi-Peptide Mix", icon: <MdOutlineAutoAwesome /> }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveIngredient(tab.id)}
                        className={`px-4 py-3 rounded-full text-xs font-semibold flex items-center gap-3 transition-all ${
                          activeIngredient === tab.id 
                            ? "bg-[#ebdcc8]/50 text-[#8c765c] border-l-2 border-[#8c765c] pl-5" 
                            : "text-gray-500 hover:text-[#8c765c]"
                        }`}
                      >
                        {tab.icon} {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Description panels */}
                  <div className="flex-1 text-left bg-white border border-[#ebdcc8] p-6 md:p-8 rounded-2xl flex flex-col justify-between">
                    {activeIngredient === "squalane" && (
                      <div className="animate-fadeIn">
                        <h4 className="text-lg font-bold font-serif text-[#3c3530]">100% Sugarcane-Derived Squalane</h4>
                        <p className="text-gray-500 text-xs mt-2 leading-relaxed">Our vegan squalane mimics skin's natural moisturizing lipids to seal in active hydration without clogging pores. Restores skin flexibility, elasticity, and radiance instantly.</p>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                          <div className="bg-[#faf6f0] border border-[#e5dcd0] p-4 rounded-xl">
                            <span className="text-2xl font-bold font-serif text-[#8c765c]">24 hrs</span>
                            <p className="text-[10px] text-gray-500 mt-1">Continuous Hydration</p>
                          </div>
                          <div className="bg-[#faf6f0] border border-[#e5dcd0] p-4 rounded-xl">
                            <span className="text-2xl font-bold font-serif text-[#8c765c]">100%</span>
                            <p className="text-[10px] text-gray-500 mt-1">Cruelty-Free & Vegan</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {activeIngredient === "rosehip" && (
                      <div className="animate-fadeIn">
                        <h4 className="text-lg font-bold font-serif text-[#3c3530]">Cold-Pressed Rosehip Fruit Extract</h4>
                        <p className="text-gray-500 text-xs mt-2 leading-relaxed">Rich in Vitamin C, pro-vitamin A, and essential fatty acids. Protects the skin barrier, actively brightens uneven pigmentation, and supports natural cellular regeneration.</p>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                          <div className="bg-[#faf6f0] border border-[#e5dcd0] p-4 rounded-xl">
                            <span className="text-2xl font-bold font-serif text-[#8c765c]">Vitamin C</span>
                            <p className="text-[10px] text-gray-500 mt-1">Deep Skin Brightening</p>
                          </div>
                          <div className="bg-[#faf6f0] border border-[#e5dcd0] p-4 rounded-xl">
                            <span className="text-2xl font-bold font-serif text-[#8c765c]">Omega 3, 6</span>
                            <p className="text-[10px] text-gray-500 mt-1">Lipid Barrier Guard</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {activeIngredient === "peptides" && (
                      <div className="animate-fadeIn">
                        <h4 className="text-lg font-bold font-serif text-[#3c3530]">Flagship Copper Peptide Chain</h4>
                        <p className="text-gray-500 text-xs mt-2 leading-relaxed">Formulated using multiple molecular weights to penetrate deeper. Rebuilds depleted skin structure, actively diminishes fine lines, and promotes natural collagen synthesis.</p>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                          <div className="bg-[#faf6f0] border border-[#e5dcd0] p-4 rounded-xl">
                            <span className="text-2xl font-bold font-serif text-[#8c765c]">Collagen +</span>
                            <p className="text-[10px] text-gray-500 mt-1">Supports Firmness</p>
                          </div>
                          <div className="bg-[#faf6f0] border border-[#e5dcd0] p-4 rounded-xl">
                            <span className="text-2xl font-bold font-serif text-[#8c765c]">Peptides</span>
                            <p className="text-[10px] text-gray-500 mt-1">Advanced Anti-Aging</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Beauty Value Badges */}
            <section className="bg-white border-y border-[#ebdcc8]/50 py-10 px-6">
              <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { title: "Pure Botanical", icon: <MdOutlineEco className="text-2xl text-[#8c765c]" />, desc: "Clean & organic formulations" },
                  { title: "Ethical Sourcing", icon: <MdOutlineSpa className="text-2xl text-[#8c765c]" />, desc: "100% sustainable supply path" },
                  { title: "Dermatology Tested", icon: <MdOutlineCheckCircle className="text-2xl text-[#8c765c]" />, desc: "Safe for sensitive skin types" },
                  { title: "Luxury Experience", icon: <MdOutlineAutoAwesome className="text-2xl text-[#8c765c]" />, desc: "Pamper yourself with premium care" }
                ].map((b, idx) => (
                  <div key={idx} className="flex flex-col items-center text-center p-4 border border-[#e5dcd0]/30 rounded-2xl bg-[#fcfaf7]">
                    <div className="p-3 rounded-full bg-white mb-3 border border-[#ebdcc8] shadow-sm">{b.icon}</div>
                    <p className="text-xs font-bold text-[#3c3530]">{b.title}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{b.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Catalog */}
        <section id="catalog-section" className="max-w-6xl mx-auto px-6 py-12 scroll-mt-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div className="text-left">
              <span className="text-xs font-bold uppercase tracking-widest text-[#8c765c]">ORGANIC CATALOG</span>
              <h3 className="text-3xl font-bold font-serif text-[#3c3530] mt-1">Radiance Collection</h3>
            </div>

            {/* Filter controls */}
            <div className="flex items-center gap-2.5">
              <button 
                onClick={() => p.setFilterPanelOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#ebdcc8] bg-white hover:bg-gray-50 text-xs font-semibold text-gray-700 transition-colors"
              >
                <MdFilterList size={16} /> Filter
              </button>
              
              <div className="rounded-full border border-[#ebdcc8] bg-white p-1 flex gap-1">
                {[
                  ["products", getTabLabel(p, "products")],
                  ["services", getTabLabel(p, "services")],
                  ["details", getTabLabel(p, "details")]
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => {
                      p.setActiveTab(k);
                      if (k === "details") {
                        navigateTo("about");
                      } else {
                        navigateTo("shop");
                      }
                    }}
                    className="px-4 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap"
                    style={p.activeTab === k ? { backgroundColor: buttonColor, color: "#fff" } : { color: "#70655c" }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full">
            <TabContent p={p} />
          </div>
        </section>
      </main>

      <StoreFooter p={p} />
    </div>
  );
}
